import React, { Fragment, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../../../../components/Header';
import PaymentsTable from '../../../../components/admin/PaymentsTable';
import PaymentsTableToolbar from '../../../../components/admin/PaymentsTableToolbar';
import { listarPagamentosPorAtleta } from '../../../../services/pagamentoInscricaoService';
import { buscarAtletaPorId, type Atleta } from '../../../../services/atletaService';
import type { PagamentoInscricao, PaymentsFilter } from '../../../../types/pagamento';
import dayjs from 'dayjs';

const AthletePaymentsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [pagamentos, setPagamentos] = useState<PagamentoInscricao[]>([]);
  const [atleta, setAtleta] = useState<Atleta | null>(null);
  const [filters, setFilters] = useState<PaymentsFilter>(() => ({ atletaId: id || null }));
  const [refresh, setRefresh] = useState<number>(0);

  // Mantém atletaId em filters sincronizado com a rota, sem disparar loops
  useEffect(() => {
    if (id && filters.atletaId !== id) {
      setFilters((prev) => ({ ...prev, atletaId: id }));
    }
  }, [id, filters.atletaId]);

  // Carrega dados quando filtros relevantes mudam
  useEffect(() => {
    const doLoad = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const [conn, atletaResp] = await Promise.all([
          listarPagamentosPorAtleta(id, {
            page: 0,
            size: 50,
            status: (filters.status as any) || undefined,
            categoriaId: filters.categoriaId || undefined,
          }),
          buscarAtletaPorId(id),
        ]);
        const items = (conn.items as unknown as PagamentoInscricao[]) || [];
        let filtered = items;
        if (filters.torneioId) {
          filtered = filtered.filter((p) => (p.inscricoes || []).some((i) => i.categoriaTorneio?.torneio?.id === filters.torneioId));
        }
        if (filters.dataInicio) {
          const start = dayjs(filters.dataInicio).startOf('day');
          filtered = filtered.filter((p) => {
            const d = dayjs(p.dataPagamento || p.criadoEm);
            return d.isValid() && (d.isAfter(start) || d.isSame(start));
          });
        }
        if (filters.dataFim) {
          const end = dayjs(filters.dataFim).endOf('day');
          filtered = filtered.filter((p) => {
            const d = dayjs(p.dataPagamento || p.criadoEm);
            return d.isValid() && (d.isBefore(end) || d.isSame(end));
          });
        }
        setPagamentos(filtered);
        setAtleta(atletaResp);
      } catch (err) {
        // erro silencioso
      } finally {
        setLoading(false);
      }
    };
    doLoad();
  }, [id, filters.status, filters.categoriaId, filters.torneioId, filters.dataInicio, filters.dataFim, refresh]);

  return (
    <Fragment>
      <div className="main-wrapper">
        <Header />
        <div className="main-content theme-dark-bg right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="middle-wrap">
                <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                  <div className="card-body p-4 w-100 bg-current border-0 d-flex rounded-3 justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <Link to="/admin/atletas" className="d-inline-block mt-2">
                        <i className="ti-arrow-left font-sm text-white"></i>
                      </Link>
                      <h4 className="font-xs text-white fw-600 ms-4 mb-0 mt-2">Pagamentos do Atleta {atleta?.usuario?.nome || (id ? `#${id}` : '')}</h4>
                    </div>
                    <button onClick={() => setRefresh((v) => v + 1)} disabled={loading} className="btn btn-sm bg-white text-dark fw-600">
                      {loading ? 'Atualizando...' : 'Atualizar'}
                    </button>
                  </div>
                </div>
                <PaymentsTableToolbar
                  availableFilters={['torneio','categoria','data','status']}
                  initialFilters={{ atletaId: id || null }}
                  value={filters}
                  onChange={setFilters}
                  onApply={() => setRefresh((v) => v + 1)}
                  onReset={() => { setFilters({ atletaId: id || null }); }}
                  showRefreshButton
                  onRefresh={() => setRefresh((v) => v + 1)}
                />
                <PaymentsTable
                  pagamentos={pagamentos}
                  loading={loading}
                  emptyMessage="Nenhum pagamento encontrado para este atleta."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AthletePaymentsPage;
